﻿import isNullOrEmpty from "../helpers/isNullOrEmpty";
import nanoid from "../helpers/nanoid";
import decodeJson from "../utils/Json";
import {HttpClient, HttpClientRequestMethod} from "./HttpClient";
import {DefaultHttpRequestOptions} from "./HttpRequestOptions";

const _contentTypeHeader: string = "content-type";
const _jsonContentType: string = "application/json";
const _jsonPatchContentType: string = "application/json-patch+json";

export interface HttpBatchRequestOptions {
	method: HttpClientRequestMethod;
	headers?: Record<string, string> | undefined;
	credentials?: RequestCredentials | undefined;
	continueOnError?: boolean;
}

export interface HttpBatchRequest<TResponse = never> {
	url: string;
	method: string;
	headers?: Record<string, string> | undefined;
	body?: any | undefined;
	_responseType?: TResponse; // phantom type for inference
}

export type HttpBatchResponse<TBody = never> = [TBody] extends [never] ? {
	status: number;
	headers?: Record<string, string> | undefined;
} : {
	status: number;
	headers?: Record<string, string> | undefined;
	body: TBody;
};

type ExtractResponseType<T> = T extends HttpBatchRequest<infer R> ? R : never;
type MapRequestsToResponses<T extends readonly HttpBatchRequest<any>[]> = {
	[K in keyof T]: HttpBatchResponse<ExtractResponseType<T[K]>>
};

interface PrivateHttpBatchRequestEntry {
	id: string;
	url: string;
	method: string;
	headers?: Record<string, string> | undefined;
	body?: string;
}

interface PrivateHttpBatchResponseEntry {
	id: string;
	status: number;
	headers?: Record<string, string> | undefined;
	body?: string;
}

interface PrivateHttpBatchResponseBody {
	responses: PrivateHttpBatchResponseEntry[];
}

class PrivateHttpClient extends HttpClient {
	constructor(baseUrl: string, defaults?: DefaultHttpRequestOptions) {
		super(baseUrl, defaults);
	}
}

export class HttpBatchHandler {
	private static _client: HttpClient;
	private static _credentials?: RequestCredentials | undefined;
	private static _method: HttpClientRequestMethod;

	public static async fetch<T extends readonly HttpBatchRequest<any>[]>(entries: T, signal?: AbortSignal | undefined): Promise<MapRequestsToResponses<T>> {
		if (this._client == null) {
			throw new Error("HttpBatchHandler.initialize must be called before using the handler");
		}

		if (entries.length === 0) {
			return Promise.resolve([] as MapRequestsToResponses<T>);
		}

		const requestIdMap = new Map<string, { index: number }>();
		const batch: PrivateHttpBatchRequestEntry[] = new Array(entries.length);
		for (let index = 0; index < entries.length; index++) {
			const normalized = this.normalize(entries[index]);
			requestIdMap.set(normalized.id, {index});
			batch[index] = normalized;
		}

		const result = await this._client.fetch("$batch", this._method, {requests: batch}, {
			credentials: this._credentials,
			signal
		});

		const responses: HttpBatchResponse[] = new Array(entries.length);

		if (result.error || !result.response.ok) {
			this.fillErrorResponses(responses);
			return responses as MapRequestsToResponses<T>;
		}

		const body = decodeJson<PrivateHttpBatchResponseBody>(await result.response.text());
		if (body == null) {
			this.fillErrorResponses(responses);
			return responses as MapRequestsToResponses<T>;
		}

		for (const response of body.responses) {
			const requestInfo = requestIdMap.get(response.id);
			if (!requestInfo) {
				continue;
			}

			// The response itself is the body signal — _responseType is a phantom TYPE field that
			// no runtime code ever assigns, so gating on it meant bodies were never parsed.
			if (response.body) {
				responses[requestInfo.index] = {
					status: response.status,
					headers: response.headers,
					body: decodeJson(response.body)
				} as HttpBatchResponse<any>;
			} else {
				responses[requestInfo.index] = {
					status: response.status,
					headers: response.headers
				} as HttpBatchResponse;
			}
		}

		this.fillErrorResponses(responses);
		return responses as MapRequestsToResponses<T>;
	}

	public static initialize(baseUrl: string, options: HttpBatchRequestOptions | undefined = undefined): void {
		this._method = options?.method ?? "POST";
		this._credentials = options?.credentials ?? "omit";
		const headers = options?.headers ?? {};
		headers[_contentTypeHeader] ??= _jsonContentType;
		headers["accept"] = _jsonContentType;
		if (options?.continueOnError == true) {
			headers["prefer"] = "odata.continue-on-error";
		}

		this._client = new PrivateHttpClient(baseUrl, {
			headers
		});
	}

	private static fillErrorResponses(responses: HttpBatchResponse[]): void {
		for (let i = 0; i < responses.length; i++) {
			if (!responses[i]) {
				responses[i] = {
					status: 500,
					headers: undefined
				};
			}
		}
	}

	private static normalize(entry: HttpBatchRequest<any>): PrivateHttpBatchRequestEntry {
		if (isNullOrEmpty(entry.url)) {
			throw new Error("Url is required");
		}

		const headers = entry.headers ?? {};
		let contentType: string | undefined;
		switch (entry.method) {
			case "POST":
			case "PUT":
				contentType = _jsonContentType;
				break;

			case "PATCH":
				contentType = _jsonPatchContentType;
				break;
		}

		if (contentType != null) {
			headers[_contentTypeHeader] = contentType;
		}

		return {
			id: nanoid(),
			url: entry.url,
			method: entry.method,
			headers,
			body: entry.body == null ? undefined : JSON.stringify(entry.body)
		}
	}
}
